import type { FrontendRoute } from "./frontend-routes";

/**
 * Determines if a route is active given the current location.
 *
 * Three matching strategies:
 * 1. External and click-action routes → always false
 * 2. Routes with searchParams (Scopes) → pathname prefix + all search params match, OR alsoMatchPaths
 * 3. Routes without searchParams (Document Library) → pathname prefix + no competing type_filter, OR alsoMatchPaths
 *
 * Home is special-cased: also matches the bare org path.
 */
export function isRouteActive(
    route: FrontendRoute,
    resolvedPathname: string,
    location: { pathname: string; search: string }
): boolean {
    if (route.isExternal || route.onClick) return false;

    const pathnameMatch =
        location.pathname === resolvedPathname ||
        location.pathname.startsWith(resolvedPathname + "/");

    const alsoMatch = route.alsoMatchPaths?.some(
        (p) => location.pathname === p || location.pathname.startsWith(p + "/")
    );

    // Home special case: also match bare org path (without /home)
    if (route.path === "home") {
        const orgBase = resolvedPathname.replace(/\/home$/, "");
        if (location.pathname === orgBase) return true;
    }

    if (route.searchParams && Object.keys(route.searchParams).length > 0) {
        const locationParams = new URLSearchParams(location.search);
        const searchMatch = Object.entries(route.searchParams).every(
            ([key, value]) => locationParams.get(key) === value
        );
        return (pathnameMatch && searchMatch) || !!alsoMatch;
    }

    // Routes without searchParams: stay inactive when type_filter targets a
    // non-documents view (scopes has its own route; comparisons has none)
    const typeFilter = new URLSearchParams(location.search).get("type_filter");
    return (
        (pathnameMatch && (!typeFilter || typeFilter === "documents")) ||
        !!alsoMatch
    );
}
